import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { LocalStorageService } from './LocalStorageService';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

api.interceptors.request.use(config => {
  const accessToken = LocalStorageService.getItem('accessToken');

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Adding the security headers
  config.headers['Strict-Transport-Security'] =
    'max-age=31536000; includeSubDomains';
  config.headers['Content-Security-Policy'] = `default-src 'self'`;
  config.headers['X-Frame-Options'] = 'sameorigin';
  config.headers['X-Content-Type-Options'] = 'nosniff';
  config.headers['Referrer-Policy'] = 'no-referrer-when-downgrade';
  config.headers['Permissions-Policy'] = 'microphone=()';

  return config;
});

class ApiBaseService {
  static async get<TResponse>(
    url: string,
    config?: AxiosRequestConfig<any>,
  ): Promise<TResponse> {
    try {
      const response = await api.get<TResponse>(url, config);

      return ApiBaseService.handleResponse(response);
    } catch (error: any) {
      return ApiBaseService.handleError(error);
    }
  }

  static async post<TResponse>(
    url: string,
    data: Record<string, any>,
    config: AxiosRequestConfig<any>,
  ): Promise<TResponse> {
    try {
      const response = await api.post(url, data, config);

      return ApiBaseService.handleResponse(response);
    } catch (error: any) {
      return ApiBaseService.handleError(error);
    }
  }

  static async patch<TResponse>(
    url: string,
    data: Record<string, any>,
    config?: AxiosRequestConfig<any>,
  ): Promise<TResponse> {
    try {
      const response = await api.patch(url, data, config);

      return ApiBaseService.handleResponse(response);
    } catch (error: any) {
      return ApiBaseService.handleError(error);
    }
  }

  static async delete(
    url: string,
    config?: AxiosRequestConfig<any>,
  ): Promise<Record<string, never>> {
    try {
      const response = await api.delete(url, config);

      return ApiBaseService.handleResponse(response);
    } catch (error: any) {
      return ApiBaseService.handleError(error);
    }
  }

  static async put(url: string, data: any, config?: AxiosRequestConfig<any>) {
    try {
      const response = await api.put(url, data, config);

      return ApiBaseService.handleResponse(response);
    } catch (error: any) {
      return ApiBaseService.handleError(error);
    }
  }

  static handleResponse(response: AxiosResponse): any {
    return response.data;
  }

  static handleError(error: AxiosError): never {
    const statusCode: any = error.response?.status;

    if (statusCode === 422) {
      const throwCode422 = {
        statusCode: 422,
        status: 'unprocessable_entity',
        message: 'Validation Failed',
      };
      throw throwCode422;
    } else {
      const responseData: any = error.response?.data;

      const checkIfResponseIsArray = Array.isArray(responseData?.message);

      const throwError = {
        statusCode: statusCode as number,
        status: responseData?.error,
        message: checkIfResponseIsArray
          ? responseData?.message?.join(',')
          : responseData?.message || responseData.data.message,
      };
      throw throwError;
    }
  }
}

export { api, ApiBaseService };
